import React, { useRef } from 'react'
import { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import { CheckPicker, PickerHandle, SelectPicker, SelectPickerProps, CheckPickerProps } from 'rsuite'
import { useFetchAllAssemblers } from '@@partner:graphql/query/employees/allAssemblers.gql'
import { Assembler } from '@@partner:types:app'

type CommonProps = {
  name?: string
  disabled?: boolean
  className?: string
  placeholder?: string
  shopIds?: GQL_ID[]
}

type MultipleProps = CommonProps & {
  isMultiple: true
  value: GQL_ID[]
  onChange: (value: GQL_ID[]) => void
  filter?: NestedFilter
}

type SingleProps = CommonProps & {
  isMultiple?: false
  value: GQL_ID | null
  onChange: (value: GQL_ID | null, item?: Assembler) => void
  filter?: NestedFilter
}

type Props = MultipleProps | SingleProps

type PickerItemType = {
  label: string
  value: string
  assembler?: Assembler
}

const prepareValue = (assemblers?: Assembler[]): PickerItemType[] => {
  if (!assemblers) return []

  return assemblers.map(assembler => ({
    label: `${assembler.first_name} ${assembler.last_name}`,
    value: String(assembler.id),
    assembler
  }))
}

export default function SelectAssemblerPicker (props: Props) {
  const ref = useRef<PickerHandle>(null)
  const [ searchText, setSearchText ] = React.useState('')

  const value = props.value
  const { isMultiple, filter: externalFilter, ...rest } = props
  const searchValues = value ? (Array.isArray(value) ? value : [ value ]).filter(Boolean) : []

  const { data, loading, error } = useFetchAllAssemblers({
    filter: searchText && searchText.length > 0 ? {
      or: [
        {
          fields: [ 'first_name', 'last_name' ],
          op: 'includes',
          value: [ searchText ]
        },
        {
          fields: [ 'id' ],
          op: 'contains',
          value: searchValues
        },
        props?.shopIds && props.shopIds.length > 0 ? {
          fields: [ 'shop_id' ],
          op: 'contains',
          value: props.shopIds
        } : {}
      ],
      ...externalFilter ? externalFilter : {}
    } : externalFilter,
    pager: {
      page: 1,
      limit: 20
    }
  })

  const { data: selectedAssemblers } = useFetchAllAssemblers({
    filter: searchValues.length > 0 ? {
      fields: [ 'id' ],
      op: 'contains',
      value: searchValues
    } : undefined,
    pager: {
      page: 1,
      limit: 20
    }
  },  !searchValues.length)

  const mergedAssemblers = [ ...(selectedAssemblers?.assemblers.edges || []), ...(data?.assemblers.edges || []) ].reduce((acc, assembler) => {
    if (!acc.some(c => c.id === assembler.id)) {
      acc.push(assembler)
    }
    return acc
  }, [] as Assembler[])
  
  const pickerData = prepareValue(mergedAssemblers)

  const handleSearch = (search: string) => {
    setSearchText(search)
  }


  if (props.isMultiple) {
    const handleMultipleChange = (newValue: GQL_ID[]) => {
      props.onChange(newValue as GQL_ID[])
    }

    return (
      <CheckPicker
        ref={ref}
        {...rest}
        value={props.value}
        data={pickerData}
        onChange={handleMultipleChange}
        searchable={true}
        onSearch={handleSearch}
        placeholder={props.placeholder || 'Все сборщики'}
        valueKey="value"
        labelKey="label"
      />
    )
  }

  const handleSingleChange = (newValue: GQL_ID | null, item: any) => {
    props.onChange(newValue as GQL_ID | null, item?.assembler as Assembler)
  }

  return (
    <SelectPicker
      ref={ref}
      {...rest}
      value={props.value}
      data={pickerData}
      onChange={handleSingleChange}
      loading={loading}
      searchable={true}
      onSearch={handleSearch}
      placeholder={props.placeholder || 'Все сборщики'}
      valueKey="value"
      labelKey="label"
    />
  )
}